import React, { useEffect, useRef, useState } from 'react';
import {
  X,
  Store,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  DollarSign,
  ChevronDown,
  Search,
  Upload,
  Download,
  Trash2,
  ShoppingCart,
  ExternalLink,
  CheckCircle2,
  Info,
  Truck,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { api } from '../../lib/api';
import { MapCoordinatePicker } from '../common/MapCoordinatePicker';
import { resolveAssetUrl } from '../../lib/assets';

type LocationOption = { id: string; name: string; code?: string; stateId?: string | null; countryId?: string | null };
type FoodTypeOption = { id: string; name: string; code?: string };
type PriceRangeOption = {
  id: string;
  code: string;
  name: string;
  symbol?: string | null;
  description?: string | null;
  displayOrder?: number;
};
type CurrencyOption = {
  id: string;
  code: string;
  name: string;
  symbol?: string | null;
  decimalPlaces?: number | null;
};

const formatPriceRangeLabel = (range: PriceRangeOption) => {
  const name = String(range.name || '').trim();
  const symbol = String(range.symbol || '').trim();
  if (!symbol) return name;

  const match = name.match(/^\s*(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*$/);
  if (!match) return `${symbol}${name}`;

  return `${symbol}${match[1]}–${symbol}${match[2]}`;
};

const slugify = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const generatePublicMenuKey = (): string => {
  try {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return Math.random().toString(16).slice(2, 18).padEnd(16, '0');
  }
};

interface AddRestaurantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string, newRestaurant?: any) => void;
  mode?: 'add' | 'edit';
  initialData?: any;
  onSubmit?: (payload: any) => Promise<any> | any;
}

type IntegrationScenario = 'full_ordering' | 'menu_redirect';
type CommissionType = 'percentage' | 'fixed';
type PayoutCycle = 'daily' | 'weekly' | 'monthly';
type DeliveryHandling = 'platform_delivery' | 'third_party';
type MenuSource = 'manual_upload' | 'api_sync';
type UpdateFrequency = 'daily' | 'weekly';

type IntegrationSetupState = {
  scenarios: IntegrationScenario[];
  fullOrdering: {
    commissionType: CommissionType;
    commissionValue: string;
    payoutCycle: PayoutCycle;
    deliveryHandling: DeliveryHandling;
    paymentMethodIds: string[];
  };
  menuRedirect: {
    websiteUrl: string;
  };
};

const hasScenario = (setup: IntegrationSetupState, scenario: IntegrationScenario) =>
  setup.scenarios.includes(scenario);

function ToggleSwitch({
  id,
  label,
  description,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-3">
      <div className="min-w-0">
        <label htmlFor={id} className="block text-sm font-medium text-gray-900">
          {label}
        </label>
        {description ? <p className="mt-1 text-xs text-gray-500">{description}</p> : null}
      </div>
      <label className="relative inline-flex cursor-pointer items-center">
        <input
          id={id}
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="h-6 w-11 rounded-full bg-gray-200 transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 peer-checked:bg-purple-600" />
        <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
      </label>
    </div>
  );
}

function InlineToggleField({
  id,
  label,
  description,
  checked,
  onChange,
  showStateLabel = true,
  onLabel = 'Enabled',
  offLabel = 'Disabled',
  variant = 'input',
  stateTone = 'normal',
  hideLabel = false,
  stateWeight = 'normal',
  stateColorClass,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  showStateLabel?: boolean;
  onLabel?: string;
  offLabel?: string;
  variant?: 'input' | 'minimal';
  stateTone?: 'normal' | 'placeholder';
  hideLabel?: boolean;
  stateWeight?: 'normal' | 'semibold';
  stateColorClass?: string;
}) {
  const stateText = checked ? onLabel : offLabel;
  const stateWeightClass = stateWeight === 'semibold' ? 'font-semibold' : 'font-medium';
  const resolvedStateColorClass =
    stateColorClass ?? (stateTone === 'placeholder' ? 'text-gray-500' : 'text-gray-900');
  return (
    <div>
      {variant === 'minimal' ? (
        <>
          <div className="inline-flex items-center gap-2">
            <label htmlFor={id} className={hideLabel ? 'sr-only' : 'text-gray-700 text-sm font-medium'}>
              {label}
            </label>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                id={id}
                type="checkbox"
                className="peer sr-only"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
              />
              <span className="h-6 w-11 rounded-full bg-gray-200 transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 peer-checked:bg-purple-600" />
              <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
              <span className="sr-only">{stateText}</span>
            </label>
          </div>
          {description ? <p className="mt-1 text-xs text-gray-500">{description}</p> : null}
        </>
      ) : (
        <>
          <label htmlFor={id} className={hideLabel ? 'sr-only' : 'block text-gray-700 text-sm font-medium mb-2'}>
            {label}
          </label>
        <div
          className={[
            'h-[42px] flex items-center gap-3 px-4 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-purple-500',
            showStateLabel ? 'justify-between' : 'justify-end',
          ].join(' ')}
        >
          {showStateLabel ? (
            <span
              className={[
                'text-sm',
                stateWeightClass,
                resolvedStateColorClass,
              ].join(' ')}
            >
              {stateText}
            </span>
          ) : (
            <span className="sr-only">{stateText}</span>
          )}
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              id={id}
              type="checkbox"
              className="peer sr-only"
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span className="h-6 w-11 rounded-full bg-gray-200 transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 peer-checked:bg-purple-600" />
            <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
          </label>
        </div>
          {description ? <p className="mt-1 text-xs text-gray-500">{description}</p> : null}
        </>
      )}
    </div>
  );
}

function ExpandSection({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={[
        'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      ].join(' ')}
      aria-hidden={!open}
    >
      <div className={open ? 'overflow-visible' : 'overflow-hidden'}>{children}</div>
    </div>
  );
}

function ScenarioCard({
  title,
  subtitle,
  icon,
  tag,
  selected,
  onToggle,
  error,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  tag?: string;
  selected: boolean;
  onToggle: () => void;
  error?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={[
        'relative w-full rounded-xl border p-4 text-left transition-all',
        'focus:outline-none focus:ring-2 focus:ring-purple-500',
        selected
          ? 'border-purple-300 bg-purple-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm',
        error && !selected ? 'border-red-300 bg-red-50/40' : '',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            'mt-0.5 rounded-lg p-2',
            selected ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700',
          ].join(' ')}
        >
          {icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
            {tag ? (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                {tag}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-gray-600">{subtitle}</p>
        </div>

        <div className="mt-0.5 flex h-6 w-6 items-center justify-center">
          {selected ? <CheckCircle2 className="h-5 w-5 text-purple-600" /> : null}
        </div>
      </div>
    </button>
  );
}

export function AddRestaurantModal({
  isOpen,
  onClose,
  onSuccess,
  mode = 'add',
  initialData,
  onSubmit,
}: AddRestaurantModalProps) {
  const { error: toastError } = useToast();
  const getInitialFormData = () => ({
    integrationSetup: {
      scenarios: [] as IntegrationScenario[],
      fullOrdering: {
        commissionType: 'percentage' as CommissionType,
        commissionValue: '',
        payoutCycle: 'weekly' as PayoutCycle,
        deliveryHandling: 'platform_delivery' as DeliveryHandling,
        paymentMethodIds: [] as string[],
      },
      menuRedirect: {
        websiteUrl: '',
      },
    } satisfies IntegrationSetupState,
    name: '',
    description: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    latitude: '',
    longitude: '',
    cityId: '',
    stateId: '',
    countryId: '',
    cuisine: [] as string[],
    foodType: [] as string[],
    priceRange: '',
    currencyId: '',
    openTime: '09:00',
    closeTime: '22:00',
    deliveryTime: '30-45',
    minimumOrder: '',
    deliveryFee: '',
    packagingFee: '',
    website: '',
    isActive: true,
    publicMenuKey: '',
    logo: '',
    coverImage: '',
  });
  const [formData, setFormData] = useState(getInitialFormData());

  const [cuisineDropdownOpen, setCuisineDropdownOpen] = useState(false);
  const [cuisineSearch, setCuisineSearch] = useState('');
  const [foodTypeDropdownOpen, setFoodTypeDropdownOpen] = useState(false);
  const [foodTypeSearch, setFoodTypeSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [cuisineOptions, setCuisineOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [countryOptions, setCountryOptions] = useState<LocationOption[]>([]);
  const [stateOptions, setStateOptions] = useState<LocationOption[]>([]);
  const [cityOptions, setCityOptions] = useState<LocationOption[]>([]);
  const [priceRangeOptions, setPriceRangeOptions] = useState<PriceRangeOption[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<CurrencyOption[]>([]);
  const [foodTypeOptions, setFoodTypeOptions] = useState<FoodTypeOption[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const cuisineDropdownRef = useRef<HTMLDivElement | null>(null);
  const foodTypeDropdownRef = useRef<HTMLDivElement | null>(null);
  const paymentMethodsDropdownRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const redirectWebsiteInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [uploadedImageFileName, setUploadedImageFileName] = useState('');
  const [uploadedLogoFileName, setUploadedLogoFileName] = useState('');
  const [showCoordinatePicker, setShowCoordinatePicker] = useState(false);
  const [resolvingMapLocation, setResolvingMapLocation] = useState(false);
  const [integrationError, setIntegrationError] = useState<string>('');
  const [paymentMethodsDropdownOpen, setPaymentMethodsDropdownOpen] = useState(false);
  const [paymentMethodsSearch, setPaymentMethodsSearch] = useState('');

  const publicMenuKeyPreview = String(
    (formData as any).publicMenuKey ||
      (mode === 'edit'
        ? (initialData?.publicMenuKey ?? initialData?.public_menu_key ?? '')
        : '') ||
      ''
  )
    .trim()
    .toLowerCase();

  const toRestaurantDomainSegment = (raw: unknown): string => {
    const cleaned = String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/.*$/, '')
      .replace(/\.[a-z]{2,}$/, '');
    const base = cleaned || String(raw || '').trim().toLowerCase();
    return base
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_');
  };
  const publicMenuBaseUrl = String(
    (import.meta as any)?.env?.VITE_PUBLIC_BASE_URL ||
      (import.meta as any)?.env?.VITE_APP_BASE_URL ||
      window.location.origin
  ).replace(/\/+$/, '');
  const publicMenuRedirectKey = publicMenuKeyPreview;
  const publicMenuRedirectSlug = toRestaurantDomainSegment(
    formData.integrationSetup?.menuRedirect?.websiteUrl || (formData as any)?.slug || (formData as any)?.name || ''
  );
  const publicMenuRedirectUrl = publicMenuRedirectKey
    ? `${publicMenuBaseUrl}/${publicMenuRedirectSlug ? `${encodeURIComponent(publicMenuRedirectSlug)}/` : ''}${encodeURIComponent(publicMenuRedirectKey)}`
    : '';

  const resolveAddressToCoords = async () => {
    const lat = Number(formData.latitude);
    const lng = Number(formData.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { latitude: lat, longitude: lng };
    }

    const addressQuery = [formData.address, formData.city, formData.state, formData.country, formData.zipCode]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(', ');
    if (!addressQuery) return null;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          addressQuery
        )}&limit=1&countrycodes=in`
      );
      if (!response.ok) return null;
      const rows = await response.json();
      const first = Array.isArray(rows) ? rows[0] : null;
      const latitude = Number(first?.lat);
      const longitude = Number(first?.lon);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      return { latitude, longitude };
    } catch {
      return null;
    }
  };

  const openMapPicker = async () => {
    setResolvingMapLocation(true);
    const coords = await resolveAddressToCoords();
    if (coords) {
      setFormData((prev) => ({
        ...prev,
        latitude: String(coords.latitude),
        longitude: String(coords.longitude),
      }));
    }
    setResolvingMapLocation(false);
    setShowCoordinatePicker(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const loadCuisineAndLocation = async () => {
      try {
        const [cuisineResult, locationResult, currencyResult, paymentMethodsResult] = await Promise.allSettled([
          api.masterData.cuisineTypes.getAll(),
          api.restaurants.getLocationOptions(),
          api.masterData.currencies.getAll(),
          api.masterData.paymentMethods.getAll(),
        ]);

        if (cuisineResult.status === 'fulfilled') {
          const cuisineList = Array.isArray(cuisineResult.value?.data) ? cuisineResult.value.data : [];
          setCuisineOptions(cuisineList.map((item: any) => ({
            id: String(item.id),
            name: String(item.name || item.title || item.slug || item.id),
          })));
        }

        if (locationResult.status === 'fulfilled') {
          const location = locationResult.value?.data || { countries: [], states: [], cities: [], priceRanges: [], foodTypes: [] };
          setCountryOptions((location.countries || []).map((item: any) => ({
            id: String(item.id),
            name: String(item.name || item.id),
            code: item.code ? String(item.code) : undefined,
          })));
          setStateOptions((location.states || []).map((item: any) => ({
            id: String(item.id),
            name: String(item.name || item.id),
            countryId: item.countryId ? String(item.countryId) : null,
          })));
          setCityOptions((location.cities || []).map((item: any) => ({
            id: String(item.id),
            name: String(item.name || item.id),
            stateId: item.stateId ? String(item.stateId) : null,
            countryId: item.countryId ? String(item.countryId) : null,
          })));
          setPriceRangeOptions((location.priceRanges || []).map((item: any) => ({
            id: String(item.id),
            code: String(item.code || ''),
            name: String(item.name || item.code || item.id),
            symbol: item.symbol ? String(item.symbol) : null,
            description: item.description ? String(item.description) : null,
            displayOrder: Number(item.displayOrder || 0),
          })));
          setFoodTypeOptions((location.foodTypes || []).map((item: any) => ({
            id: String(item.id),
            name: String(item.name || item.id),
            code: item.code ? String(item.code) : undefined,
          })));
        }

        const currencies = currencyResult.status === 'fulfilled' && Array.isArray(currencyResult.value?.data)
          ? currencyResult.value.data
          : [];
        const mappedCurrencies = currencies.map((item: any) => ({
          id: String(item.id),
          code: String(item.code || item.currency_code || ''),
          name: String(item.name || item.currency_name || item.code || item.id),
          symbol: item.symbol ? String(item.symbol) : null,
          decimalPlaces:
            item.decimalPlaces !== undefined && item.decimalPlaces !== null
              ? Number(item.decimalPlaces)
              : item.decimal_places !== undefined && item.decimal_places !== null
                ? Number(item.decimal_places)
                : null,
        }));
        setCurrencyOptions(mappedCurrencies);
        setFormData((prev) => {
          if (prev.currencyId || mode === 'edit') return prev;
          const preferred =
            mappedCurrencies.find((curr) => String(curr.code || '').toUpperCase() === 'INR') ||
            mappedCurrencies[0];
          return preferred ? { ...prev, currencyId: preferred.id } : prev;
        });

        if (paymentMethodsResult.status === 'fulfilled') {
          const paymentMethodsList = Array.isArray(paymentMethodsResult.value?.data) ? paymentMethodsResult.value.data : [];
          setPaymentMethodOptions(
            paymentMethodsList
              .filter((item: any) => Boolean(item?.isActive ?? item?.is_active ?? true))
              .map((item: any) => ({
                id: String(item.id),
                name: String(item.name || item.title || item.label || item.code || item.id),
                code: item.code ? String(item.code) : undefined,
              }))
          );
        } else {
          console.error('Failed to load payment methods', paymentMethodsResult.reason);
        }
      } catch (err: any) {
        console.error('Failed to load dropdown data', err);
        toastError(err?.message || 'Failed to load dropdown data');
      }
    };
    void loadCuisineAndLocation();
  }, [isOpen, mode, toastError]);

  const loadLocationOptions = async (params?: { countryId?: string; stateId?: string }) => {
    try {
      const response = await api.restaurants.getLocationOptions(params);
      const location = response?.data || { countries: [], states: [], cities: [], priceRanges: [], foodTypes: [] };
      setCountryOptions((location.countries || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
        code: item.code ? String(item.code) : undefined,
      })));
      setStateOptions((location.states || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
        countryId: item.countryId ? String(item.countryId) : null,
      })));
      setCityOptions((location.cities || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
        stateId: item.stateId ? String(item.stateId) : null,
        countryId: item.countryId ? String(item.countryId) : null,
      })));
      setPriceRangeOptions((location.priceRanges || []).map((item: any) => ({
        id: String(item.id),
        code: String(item.code || ''),
        name: String(item.name || item.code || item.id),
        symbol: item.symbol ? String(item.symbol) : null,
        description: item.description ? String(item.description) : null,
        displayOrder: Number(item.displayOrder || 0),
      })));
      setFoodTypeOptions((location.foodTypes || []).map((item: any) => ({
        id: String(item.id),
        name: String(item.name || item.id),
        code: item.code ? String(item.code) : undefined,
      })));
    } catch (err: any) {
      toastError(err?.message || 'Failed to load location options');
    }
  };

  useEffect(() => {
    if (!cuisineDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!cuisineDropdownRef.current) return;
      const target = event.target as Node;
      if (!cuisineDropdownRef.current.contains(target)) {
        setCuisineDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cuisineDropdownOpen]);

  useEffect(() => {
    if (!foodTypeDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!foodTypeDropdownRef.current) return;
      const target = event.target as Node;
      if (!foodTypeDropdownRef.current.contains(target)) {
        setFoodTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [foodTypeDropdownOpen]);

  useEffect(() => {
    if (!paymentMethodsDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!paymentMethodsDropdownRef.current) return;
      const target = event.target as Node;
      if (!paymentMethodsDropdownRef.current.contains(target)) {
        setPaymentMethodsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [paymentMethodsDropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setIntegrationError('');
    if (mode !== 'edit' || !initialData) {
      setFormData(getInitialFormData());
      return;
    }

    const cuisine = Array.isArray(initialData?.cuisine)
      ? initialData.cuisine.map((item: any) => String(item).trim()).filter(Boolean)
      : typeof initialData?.cuisineType === 'string'
        ? initialData.cuisineType.split(',').map((item: string) => item.trim()).filter(Boolean)
        : [];
    const foodType = Array.isArray(initialData?.foodTypeIds)
      ? initialData.foodTypeIds.map((item: any) => String(item).trim()).filter(Boolean)
      : Array.isArray(initialData?.foodTypes)
        ? initialData.foodTypes.map((item: any) => String(item?.id ?? item).trim()).filter(Boolean)
        : [];

    const coverImageRaw = initialData?.coverImage || initialData?.banner || '';
    const coverImageValue = resolveAssetUrl(coverImageRaw) || '';
    const fileNameFromPath = String(coverImageValue || coverImageRaw || '')
      .split('?')[0]
      .split('/')
      .pop() || '';
    const logoRaw = initialData?.logo || '';
    const logoValue = resolveAssetUrl(logoRaw) || '';
    const logoFileNameFromPath = String(logoValue || logoRaw || '')
      .split('?')[0]
      .split('/')
      .pop() || '';

    const baseIntegrationSetup = getInitialFormData().integrationSetup;
    const incomingIntegrationSetup = initialData?.integrationSetup || initialData?.integration_setup;
    const resolvedIntegrationSetup = incomingIntegrationSetup && typeof incomingIntegrationSetup === 'object'
      ? {
        ...baseIntegrationSetup,
        scenarios: Array.isArray(incomingIntegrationSetup?.scenarios)
          ? incomingIntegrationSetup.scenarios.map((s: any) => String(s || '').trim()).filter(Boolean)
          : baseIntegrationSetup.scenarios,
        fullOrdering: {
          ...baseIntegrationSetup.fullOrdering,
          ...(incomingIntegrationSetup?.fullOrdering && typeof incomingIntegrationSetup.fullOrdering === 'object'
            ? incomingIntegrationSetup.fullOrdering
            : {}),
          paymentMethodIds: Array.isArray(incomingIntegrationSetup?.fullOrdering?.paymentMethodIds)
            ? incomingIntegrationSetup.fullOrdering.paymentMethodIds.map((id: any) => String(id || '').trim()).filter(Boolean)
            : baseIntegrationSetup.fullOrdering.paymentMethodIds,
        },
        menuRedirect: {
          ...baseIntegrationSetup.menuRedirect,
          ...(incomingIntegrationSetup?.menuRedirect && typeof incomingIntegrationSetup.menuRedirect === 'object'
            ? incomingIntegrationSetup.menuRedirect
            : {}),
        },
      }
      : baseIntegrationSetup;

    const initialRedirectUrl = String(
      initialData?.redirectUrl ?? initialData?.redirect_url ?? ''
    ).trim();
    const existingRedirectKeyMatch =
      initialRedirectUrl.match(/^([a-f0-9]{16})$/i) ||
      initialRedirectUrl.match(/^\/r\/([a-f0-9]{16})$/i);
    const existingFromRedirectUrl = existingRedirectKeyMatch?.[1]
      ? String(existingRedirectKeyMatch[1]).toLowerCase()
      : '';

    const resolvedPublicMenuKey =
      String(initialData?.publicMenuKey ?? initialData?.public_menu_key ?? '').trim().toLowerCase() ||
      existingFromRedirectUrl ||
      generatePublicMenuKey();

    setFormData({
      integrationSetup: resolvedIntegrationSetup,
      name: initialData?.name || '',
      description: initialData?.description || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      address: initialData?.address || '',
      city: initialData?.city || '',
      state: initialData?.state || '',
      zipCode: initialData?.zipCode || initialData?.pincode || '',
      country: initialData?.country || 'India',
      latitude:
        initialData?.latitude !== undefined && initialData?.latitude !== null
          ? String(initialData.latitude)
          : '',
      longitude:
        initialData?.longitude !== undefined && initialData?.longitude !== null
          ? String(initialData.longitude)
          : '',
      cityId: initialData?.locationMaster?.cityId || initialData?.cityId || initialData?.city_id || '',
      stateId: initialData?.locationMaster?.stateId || initialData?.stateId || initialData?.state_id || '',
      countryId: initialData?.locationMaster?.countryId || initialData?.countryId || initialData?.country_id || '',
      cuisine,
      foodType,
      priceRange:
        initialData?.priceRangeId ||
        initialData?.price_range_id ||
        initialData?.priceRangeMaster?.id ||
        '',
      currencyId:
        initialData?.currencyId ||
        initialData?.currency_id ||
        initialData?.currency?.id ||
        initialData?.currencyMaster?.id ||
        '',
      openTime: initialData?.openTime || initialData?.open_time || '09:00',
      closeTime: initialData?.closeTime || initialData?.close_time || '22:00',
      deliveryTime: initialData?.deliveryTime || initialData?.delivery_time || '30-45',
      minimumOrder: String(initialData?.minimumOrder ?? initialData?.minimum_order ?? ''),
      deliveryFee: String(initialData?.deliveryFee ?? initialData?.delivery_fee ?? ''),
      packagingFee: String(initialData?.packagingFee ?? initialData?.packagingCharges ?? initialData?.packaging_charges ?? ''),
      website: initialData?.website || '',
      isActive: Boolean(initialData?.isActive ?? initialData?.is_active ?? true),
      publicMenuKey: resolvedPublicMenuKey,
      logo: logoValue,
      coverImage: coverImageValue,
    });
    setUploadedImageFileName(fileNameFromPath);
    setUploadedLogoFileName(logoFileNameFromPath);
    setPendingImageFile(null);
    setPendingLogoFile(null);
  }, [isOpen, mode, initialData]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit') return;
    setFormData((prev: any) => {
      const existing = String(prev?.publicMenuKey ?? '').trim();
      if (existing) return prev;
      return { ...prev, publicMenuKey: generatePublicMenuKey() };
    });
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !initialData) return;
    if (!formData.countryId && !formData.stateId && !formData.cityId) return;
    void loadLocationOptions({
      countryId: formData.countryId || undefined,
      stateId: formData.stateId || undefined,
    });
  }, [isOpen, mode, initialData, formData.countryId, formData.stateId, formData.cityId]);

  const priceRanges = priceRangeOptions;
  const selectedCurrency = currencyOptions.find((currency) => currency.id === formData.currencyId);
  const currencySymbol = selectedCurrency?.symbol || selectedCurrency?.code || '₹';

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toastError('Please upload a valid image file');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const response = await api.restaurants.uploadImage({
        imageData: dataUrl,
        fileName: file.name,
      });
      const uploadedUrl = response?.data?.url || '';
      const absoluteUrl = resolveAssetUrl(uploadedUrl);
      setFormData((prev) => ({ ...prev, coverImage: absoluteUrl || uploadedUrl }));
      setUploadedImageFileName(response?.data?.fileName || file.name);
      setPendingImageFile(null);
    } catch (err) {
      toastError('Unable to upload selected image');
    }
  };

  const handleImageDownload = async () => {
    if (!formData.coverImage) return;
    try {
      const response = await fetch(resolveAssetUrl(formData.coverImage) || formData.coverImage);
      if (!response.ok) {
        throw new Error('Failed to download image');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const fileNameFromUrl = String(resolveAssetUrl(formData.coverImage) || formData.coverImage)
        .split('?')[0]
        .split('/')
        .pop() || 'restaurant-image';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = uploadedImageFileName || fileNameFromUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toastError('Unable to download image');
    }
  };

  const handleLogoUpload = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toastError('Please upload a valid image file');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const response = await api.restaurants.uploadImage({
        imageData: dataUrl,
        fileName: file.name,
      });
      const uploadedUrl = response?.data?.url || '';
      const absoluteUrl = resolveAssetUrl(uploadedUrl);
      setFormData((prev) => ({ ...prev, logo: absoluteUrl || uploadedUrl }));
      setUploadedLogoFileName(response?.data?.fileName || file.name);
      setPendingLogoFile(null);
    } catch (err) {
      toastError('Unable to upload selected logo');
    }
  };

  const handleLogoDownload = async () => {
    if (!formData.logo) return;
    try {
      const response = await fetch(resolveAssetUrl(formData.logo) || formData.logo);
      if (!response.ok) {
        throw new Error('Failed to download logo');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const fileNameFromUrl = String(resolveAssetUrl(formData.logo) || formData.logo)
        .split('?')[0]
        .split('/')
        .pop() || 'restaurant-logo';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = uploadedLogoFileName || fileNameFromUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toastError('Unable to download logo');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.integrationSetup.scenarios.length === 0) {
      setIntegrationError('Select at least one integration scenario to continue.');
      toastError('Integration Setup: please select at least one option');
      return;
    }

    if (
      hasScenario(formData.integrationSetup, 'menu_redirect') &&
      !String(formData.integrationSetup.menuRedirect.websiteUrl || '').trim()
    ) {
      setIntegrationError('Website URL is required for Menu Only (Redirect).');
      toastError('Integration Setup: Website URL is required');
      redirectWebsiteInputRef.current?.focus();
      return;
    }

    setLoading(true);

    try {
      const payload = {
        integrationSetup: formData.integrationSetup,
        publicMenuKey: (formData as any).publicMenuKey,
        name: formData.name,
        description: formData.description,
        logo: formData.logo || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(formData.name)}`,
        banner: formData.coverImage || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=400&fit=crop',
        coverImage: formData.coverImage || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=400&fit=crop',
        address: formData.address,
        cityId: formData.cityId,
        stateId: formData.stateId,
        countryId: formData.countryId,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        latitude:
          formData.latitude.trim() === '' || Number.isNaN(Number(formData.latitude))
            ? undefined
            : Number(formData.latitude),
        longitude:
          formData.longitude.trim() === '' || Number.isNaN(Number(formData.longitude))
            ? undefined
            : Number(formData.longitude),
        pincode: formData.zipCode || '',
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        priceRangeId: formData.priceRange,
        currencyId: formData.currencyId || undefined,
        foodTypeIds: formData.foodType,
        cuisineType: formData.cuisine.join(','),
        deliveryTime: formData.deliveryTime,
        minimumOrder: Number(formData.minimumOrder) || 0,
        deliveryFee: Number(formData.deliveryFee) || 0,
        packagingCharges: Number(formData.packagingFee) || 0,
        openTime: formData.openTime,
        closeTime: formData.closeTime,
        isActive: formData.isActive,
      };

      const response = mode === 'edit'
        ? (onSubmit ? await onSubmit(payload) : await api.restaurants.update(String(initialData?.id || ''), payload))
        : await api.restaurants.create(payload);

      onSuccess(mode === 'edit' ? 'Restaurant updated successfully!' : 'Restaurant added successfully!', response?.data ?? payload);
      handleClose();
    } catch (submitError: any) {
      console.error('Error saving restaurant:', submitError);
      toastError(submitError?.message || `Error ${mode === 'edit' ? 'updating' : 'adding'} restaurant`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(getInitialFormData());
    setCuisineSearch('');
    setCuisineDropdownOpen(false);
    setFoodTypeSearch('');
    setFoodTypeDropdownOpen(false);
    setPaymentMethodsSearch('');
    setPaymentMethodsDropdownOpen(false);
    setPendingImageFile(null);
    setPendingLogoFile(null);
    setUploadedImageFileName('');
    setUploadedLogoFileName('');
    setIntegrationError('');
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (logoInputRef.current) logoInputRef.current.value = '';
    onClose();
  };

  const toggleScenario = (scenario: IntegrationScenario) => {
    setFormData((prev) => {
      const exists = prev.integrationSetup.scenarios.includes(scenario);
      const nextScenarios = exists
        ? prev.integrationSetup.scenarios.filter((s) => s !== scenario)
        : [...prev.integrationSetup.scenarios, scenario];
      return {
        ...prev,
        integrationSetup: {
          ...prev.integrationSetup,
          scenarios: nextScenarios,
        },
      };
    });
    setIntegrationError('');
  };

  const paymentMethodNameById = paymentMethodOptions.reduce((acc, item) => {
    acc[item.id] = item.name;
    return acc;
  }, {} as Record<string, string>);

  const selectedPaymentMethodNames = formData.integrationSetup.fullOrdering.paymentMethodIds
    .map((id) => paymentMethodNameById[id] || id)
    .filter(Boolean);

  const filteredPaymentMethodOptions = paymentMethodOptions.filter((item) => {
    const q = paymentMethodsSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      item.name.toLowerCase().includes(q) ||
      String(item.code || '').toLowerCase().includes(q)
    );
  });

  const paymentMethodsDropdownLabel = selectedPaymentMethodNames.length === 0
    ? 'Select payment methods'
    : selectedPaymentMethodNames.length <= 3
      ? selectedPaymentMethodNames.join(', ')
      : `${selectedPaymentMethodNames.length} methods selected`;

  const allVisiblePaymentMethodsSelected =
    filteredPaymentMethodOptions.length > 0 &&
    filteredPaymentMethodOptions.every((item) =>
      formData.integrationSetup.fullOrdering.paymentMethodIds.includes(item.id)
    );

  const toggleAllVisiblePaymentMethods = () => {
    const visibleIds = filteredPaymentMethodOptions.map((item) => item.id).filter(Boolean);
    setFormData((prev) => {
      const selected = prev.integrationSetup.fullOrdering.paymentMethodIds;
      const shouldClear = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
      const nextIds = shouldClear
        ? selected.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...selected, ...visibleIds]));
      return {
        ...prev,
        integrationSetup: {
          ...prev.integrationSetup,
          fullOrdering: {
            ...prev.integrationSetup.fullOrdering,
            paymentMethodIds: nextIds,
          },
        },
      };
    });
  };

  const togglePaymentMethod = (paymentMethodId: string) => {
    if (!paymentMethodId) return;
    setFormData((prev) => {
      const exists = prev.integrationSetup.fullOrdering.paymentMethodIds.includes(paymentMethodId);
      const nextIds = exists
        ? prev.integrationSetup.fullOrdering.paymentMethodIds.filter((id) => id !== paymentMethodId)
        : [...prev.integrationSetup.fullOrdering.paymentMethodIds, paymentMethodId];
      return {
        ...prev,
        integrationSetup: {
          ...prev.integrationSetup,
          fullOrdering: {
            ...prev.integrationSetup.fullOrdering,
            paymentMethodIds: nextIds,
          },
        },
      };
    });
  };

  const selectAllVisiblePaymentMethods = () => {
    const visibleIds = filteredPaymentMethodOptions.map((item) => item.id).filter(Boolean);
    setFormData((prev) => {
      const union = Array.from(new Set([...prev.integrationSetup.fullOrdering.paymentMethodIds, ...visibleIds]));
      return {
        ...prev,
        integrationSetup: {
          ...prev.integrationSetup,
          fullOrdering: {
            ...prev.integrationSetup.fullOrdering,
            paymentMethodIds: union,
          },
        },
      };
    });
  };

  const clearPaymentMethods = () => {
    setFormData((prev) => ({
      ...prev,
      integrationSetup: {
        ...prev.integrationSetup,
        fullOrdering: {
          ...prev.integrationSetup.fullOrdering,
          paymentMethodIds: [],
        },
      },
    }));
  };

  const toggleCuisine = (cuisineId: string) => {
    if (!cuisineId) return;
    setFormData((prev) => {
      const exists = prev.cuisine.includes(cuisineId);
      return {
        ...prev,
        cuisine: exists
          ? prev.cuisine.filter((id) => id !== cuisineId)
          : [...prev.cuisine, cuisineId],
      };
    });
  };

  const toggleFoodType = (foodTypeId: string) => {
    if (!foodTypeId) return;
    setFormData((prev) => {
      const exists = prev.foodType.includes(foodTypeId);
      return {
        ...prev,
        foodType: exists
          ? prev.foodType.filter((id) => id !== foodTypeId)
          : [...prev.foodType, foodTypeId],
      };
    });
  };

  const toggleAllVisibleCuisines = () => {
    const visibleIds = filteredCuisineOptions.map((item) => item.id).filter(Boolean);
    if (visibleIds.length === 0) return;

    setFormData((prev) => {
      const selectedSet = new Set(prev.cuisine);
      const allVisibleSelected = visibleIds.every((id) => selectedSet.has(id));

      if (allVisibleSelected) {
        return {
          ...prev,
          cuisine: prev.cuisine.filter((id) => !visibleIds.includes(id)),
        };
      }

      visibleIds.forEach((id) => selectedSet.add(id));
      return {
        ...prev,
        cuisine: Array.from(selectedSet),
      };
    });
  };

  const toggleAllVisibleFoodTypes = () => {
    const visibleIds = filteredFoodTypeOptions.map((item) => item.id).filter(Boolean);
    if (visibleIds.length === 0) return;

    setFormData((prev) => {
      const selectedSet = new Set(prev.foodType);
      const allVisibleSelected = visibleIds.every((id) => selectedSet.has(id));

      if (allVisibleSelected) {
        return {
          ...prev,
          foodType: prev.foodType.filter((id) => !visibleIds.includes(id)),
        };
      }

      visibleIds.forEach((id) => selectedSet.add(id));
      return {
        ...prev,
        foodType: Array.from(selectedSet),
      };
    });
  };

  const cuisineNameById = cuisineOptions.reduce((acc, item) => {
    acc[item.id] = item.name;
    return acc;
  }, {} as Record<string, string>);

  const filteredCuisineOptions = cuisineOptions.filter((item) =>
    item.name.toLowerCase().includes(cuisineSearch.trim().toLowerCase())
  );
  const allVisibleCuisinesSelected =
    filteredCuisineOptions.length > 0 &&
    filteredCuisineOptions.every((item) => formData.cuisine.includes(item.id));

  const selectedCuisineNames = formData.cuisine
    .map((id) => cuisineNameById[id] || id)
    .filter(Boolean);
  const cuisineDropdownLabel = selectedCuisineNames.length === 0
    ? 'Select cuisine types'
    : selectedCuisineNames.length <= 3
      ? selectedCuisineNames.join(', ')
      : `${selectedCuisineNames.length} items selected`;

  const foodTypeNameById = foodTypeOptions.reduce((acc, item) => {
    acc[item.id] = item.name;
    return acc;
  }, {} as Record<string, string>);

  const filteredFoodTypeOptions = foodTypeOptions.filter((item) =>
    item.name.toLowerCase().includes(foodTypeSearch.trim().toLowerCase())
  );
  const allVisibleFoodTypesSelected =
    filteredFoodTypeOptions.length > 0 &&
    filteredFoodTypeOptions.every((item) => formData.foodType.includes(item.id));

  const selectedFoodTypeNames = formData.foodType
    .map((id) => foodTypeNameById[id] || id)
    .filter(Boolean);
  const foodTypeDropdownLabel = selectedFoodTypeNames.length === 0
    ? 'Select food type'
    : selectedFoodTypeNames.length <= 3
      ? selectedFoodTypeNames.join(', ')
      : `${selectedFoodTypeNames.length} items selected`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white/10 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Store className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-white text-xl font-semibold">
                {mode === 'edit' ? 'Edit Restaurant' : 'Add New Restaurant'}
              </h2>
              <p className="text-purple-100 text-sm">
                {mode === 'edit' ? 'Update restaurant details below' : 'Fill in the restaurant details below'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Integration Setup */}
          <div className="rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-gray-900 font-semibold flex items-center gap-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Truck className="w-5 h-5 text-purple-700" />
                  </div>
                  Integration Setup
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Choose how customers can discover and order from this restaurant. You can enable both.
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
                <span className="rounded-full bg-gray-100 px-2 py-1">Multi-select</span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <ScenarioCard
                title="Full Ordering System"
                subtitle="Customers can order, pay & track within platform"
                tag="Recommended"
                icon={<ShoppingCart className="h-5 w-5" />}
                selected={hasScenario(formData.integrationSetup, 'full_ordering')}
                onToggle={() => toggleScenario('full_ordering')}
                error={Boolean(integrationError)}
              />
              <ScenarioCard
                title="Menu Only (Redirect)"
                subtitle="Customers are redirected to restaurant website"
                icon={<ExternalLink className="h-5 w-5" />}
                selected={hasScenario(formData.integrationSetup, 'menu_redirect')}
                onToggle={() => toggleScenario('menu_redirect')}
                error={Boolean(integrationError)}
              />
            </div>

            {hasScenario(formData.integrationSetup, 'full_ordering') && hasScenario(formData.integrationSetup, 'menu_redirect') ? (
              <div className="mt-4 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-blue-900">
                <Info className="h-5 w-5 mt-0.5 text-blue-700" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Dual ordering enabled</p>
                  <p className="text-sm text-blue-800">
                    Customers can order via platform or external website.
                  </p>
                </div>
              </div>
            ) : null}

            {integrationError ? (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {integrationError}
              </p>
            ) : null}

            <div className="mt-4 space-y-4">
              <ExpandSection open={hasScenario(formData.integrationSetup, 'full_ordering')}>
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Full Ordering System configuration</p>
                    <p className="mt-1 text-xs text-gray-500">Commission, payouts, delivery and payment setup.</p>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="commissionType" className="block text-gray-700 text-sm font-medium mb-2">
                        Commission Type
                      </label>
                      <select
                        id="commissionType"
                        value={formData.integrationSetup.fullOrdering.commissionType}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            integrationSetup: {
                              ...prev.integrationSetup,
                              fullOrdering: {
                                ...prev.integrationSetup.fullOrdering,
                                commissionType: e.target.value as CommissionType,
                              },
                            },
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="commissionValue" className="block text-gray-700 text-sm font-medium mb-2">
                        Commission Value
                      </label>
                      <input
                        id="commissionValue"
                        type="text"
                        value={formData.integrationSetup.fullOrdering.commissionValue}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            integrationSetup: {
                              ...prev.integrationSetup,
                              fullOrdering: {
                                ...prev.integrationSetup.fullOrdering,
                                commissionValue: e.target.value,
                              },
                            },
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder={formData.integrationSetup.fullOrdering.commissionType === 'percentage' ? 'e.g. 18' : 'e.g. 25'}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {formData.integrationSetup.fullOrdering.commissionType === 'percentage'
                          ? 'Enter percentage (without %).'
                          : 'Enter fixed amount.'}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="payoutCycle" className="block text-gray-700 text-sm font-medium mb-2">
                        Payout Cycle
                      </label>
                      <select
                        id="payoutCycle"
                        value={formData.integrationSetup.fullOrdering.payoutCycle}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            integrationSetup: {
                              ...prev.integrationSetup,
                              fullOrdering: {
                                ...prev.integrationSetup.fullOrdering,
                                payoutCycle: e.target.value as PayoutCycle,
                              },
                            },
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="deliveryHandling" className="block text-gray-700 text-sm font-medium mb-2">
                        Delivery Handling
                      </label>
                      <select
                        id="deliveryHandling"
                        value={formData.integrationSetup.fullOrdering.deliveryHandling}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            integrationSetup: {
                              ...prev.integrationSetup,
                              fullOrdering: {
                                ...prev.integrationSetup.fullOrdering,
                                deliveryHandling: e.target.value as DeliveryHandling,
                              },
                            },
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                      >
                        <option value="platform_delivery">Platform Delivery</option>
                        <option value="third_party">3rd Party (Zomato API / Logistics)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">Payment Methods</label>
                      <div ref={paymentMethodsDropdownRef} className="relative">
                        <button
                          type="button"
                          onClick={() => setPaymentMethodsDropdownOpen((prev) => !prev)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <span
                            className={`${selectedPaymentMethodNames.length ? 'text-gray-900' : 'text-gray-500'} truncate pr-3`}
                            title={selectedPaymentMethodNames.join(', ')}
                          >
                            {paymentMethodsDropdownLabel}
                          </span>
                          <ChevronDown
                            className={[
                              'w-4 h-4 text-gray-500 transition-transform',
                              paymentMethodsDropdownOpen ? 'rotate-180' : '',
                            ].join(' ')}
                          />
                        </button>

                        {paymentMethodsDropdownOpen && (
                          <div className="absolute left-0 right-0 top-full z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl">
                            <div className="p-2 border-b border-gray-100">
                              <div className="flex items-center gap-2">
                                <label className="inline-flex items-center justify-center px-2 py-2 border border-gray-300 rounded-lg cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={allVisiblePaymentMethodsSelected}
                                    onChange={toggleAllVisiblePaymentMethods}
                                    className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                </label>
                                <div className="relative flex-1">
                                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                  <input
                                    type="text"
                                    value={paymentMethodsSearch}
                                    onChange={(e) => setPaymentMethodsSearch(e.target.value)}
                                    placeholder="Search payment methods..."
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                  />
                                  {paymentMethodsSearch && (
                                    <button
                                      type="button"
                                      onClick={() => setPaymentMethodsSearch('')}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                              {filteredPaymentMethodOptions.length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-500">No payment method found</div>
                              )}
                              {filteredPaymentMethodOptions.map((method) => {
                                const checked = formData.integrationSetup.fullOrdering.paymentMethodIds.includes(method.id);
                                const secondary = method.code ? ` (${method.code})` : '';
                                return (
                                  <label
                                    key={method.id}
                                    className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-purple-50 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => togglePaymentMethod(method.id)}
                                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span className="text-sm text-gray-800">
                                      {method.name}
                                      {secondary}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Selected payment method chips intentionally hidden (dropdown label shows selection count). */}
                      </div>
                    </div>
                  </div>
                </div>
              </ExpandSection>

              <ExpandSection open={hasScenario(formData.integrationSetup, 'menu_redirect')}>
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Menu Only (Redirect) configuration</p>
                    <p className="mt-1 text-xs text-gray-500">Website redirect, label and menu sync options.</p>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="redirectWebsiteUrl" className="block text-gray-700 text-sm font-medium mb-2">
                        Website URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        ref={redirectWebsiteInputRef}
                        id="redirectWebsiteUrl"
                        type="url"
                        required={hasScenario(formData.integrationSetup, 'menu_redirect')}
                        value={formData.integrationSetup.menuRedirect.websiteUrl}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            integrationSetup: {
                              ...prev.integrationSetup,
                              menuRedirect: {
                                ...prev.integrationSetup.menuRedirect,
                                websiteUrl: value,
                              },
                            },
                          }));
                          setIntegrationError('');
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="https://www.restaurant.com"
                      />
                      <p className="mt-1 text-xs text-gray-500">Used for the redirect button in customer app.</p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Redirect URL
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          value={publicMenuRedirectKey}
                          readOnly
                          placeholder="Auto-generated menu link"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 focus:outline-none"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Auto-generated public menu link (unique 16-character key).
                      </p>
                    </div>

                  </div>
                </div>
              </ExpandSection>
            </div>
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <Store className="w-5 h-5 text-purple-600" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Restaurant Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter restaurant name"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="Brief description about the restaurant"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="restaurant@example.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="+91 1234567890"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://www.restaurant.com"
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              Image Upload
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                {!formData.coverImage && (
                  <div className="flex items-center gap-3">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPendingImageFile(e.target.files?.[0] || null)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => void handleImageUpload(pendingImageFile || undefined)}
                      disabled={!pendingImageFile}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                    >
                      <Upload className="w-4 h-4" />
                      Upload
                    </button>
                  </div>
                )}
                {formData.coverImage && (
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      readOnly
                      value={uploadedImageFileName || 'uploaded-image'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={() => void handleImageDownload()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, coverImage: '' }));
                        setUploadedImageFileName('');
                        setPendingImageFile(null);
                        if (imageInputRef.current) imageInputRef.current.value = '';
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Logo Upload
                </label>
                {!formData.logo && (
                  <div className="flex items-center gap-3">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPendingLogoFile(e.target.files?.[0] || null)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => void handleLogoUpload(pendingLogoFile || undefined)}
                      disabled={!pendingLogoFile}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                    >
                      <Upload className="w-4 h-4" />
                      Upload
                    </button>
                  </div>
                )}
                {formData.logo && (
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      readOnly
                      value={uploadedLogoFileName || 'uploaded-logo'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={() => void handleLogoDownload()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, logo: '' }));
                        setUploadedLogoFileName('');
                        setPendingLogoFile(null);
                        if (logoInputRef.current) logoInputRef.current.value = '';
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Location Details */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Location Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.countryId}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selected = countryOptions.find((option) => option.id === value);
                    setFormData({
                      ...formData,
                      countryId: value,
                      country: selected?.name || '',
                      stateId: '',
                      state: '',
                      cityId: '',
                      city: '',
                    });
                    void loadLocationOptions({ countryId: value || undefined });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select Country</option>
                  {countryOptions.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  disabled={!formData.countryId}
                  value={formData.stateId}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selected = stateOptions.find((option) => option.id === value);
                    setFormData({
                      ...formData,
                      stateId: value,
                      state: selected?.name || '',
                      cityId: '',
                      city: '',
                    });
                    void loadLocationOptions({
                      countryId: formData.countryId || undefined,
                      stateId: value || undefined,
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">Select State</option>
                  {stateOptions.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  disabled={!formData.stateId}
                  value={formData.cityId}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selected = cityOptions.find((option) => option.id === value);
                    setFormData({
                      ...formData,
                      cityId: value,
                      city: selected?.name || '',
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">Select City</option>
                  {cityOptions.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  ZIP Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="123456"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Latitude
                </label>
                <input
                  type="text"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g. 28.6139"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Longitude
                </label>
                <input
                  type="text"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g. 77.2090"
                />
              </div>

              <div className="md:col-span-2">
                <button
                  type="button"
                  onClick={() => void openMapPicker()}
                  disabled={resolvingMapLocation}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-60"
                >
                  <MapPin className="w-4 h-4" />
                  {resolvingMapLocation ? 'Loading Map...' : 'View / Pick Location on Map'}
                </button>
              </div>
            </div>
          </div>

          {/* Cuisine & Pricing */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              Cuisine & Pricing
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Cuisine Types <span className="text-red-500">*</span>
                </label>
                <div className="mb-2 relative" ref={cuisineDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setCuisineDropdownOpen((prev) => !prev)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <span
                      className={`${formData.cuisine.length ? 'text-gray-900' : 'text-gray-500'} truncate pr-3`}
                      title={selectedCuisineNames.join(', ')}
                    >
                      {cuisineDropdownLabel}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${cuisineDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {cuisineDropdownOpen && (
                    <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl">
                      <div className="p-2 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <label className="inline-flex items-center justify-center px-2 py-2 border border-gray-300 rounded-lg cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={allVisibleCuisinesSelected}
                              onChange={toggleAllVisibleCuisines}
                              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                          </label>
                          <div className="relative flex-1">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={cuisineSearch}
                            onChange={(e) => setCuisineSearch(e.target.value)}
                            placeholder="Search cuisine..."
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                          {cuisineSearch && (
                            <button
                              type="button"
                              onClick={() => setCuisineSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                          </div>
                        </div>
                      </div>
                      <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                        {filteredCuisineOptions.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">No cuisine found</div>
                        )}
                        {filteredCuisineOptions.map((cuisine) => {
                          const checked = formData.cuisine.includes(cuisine.id);
                          return (
                            <label
                              key={cuisine.id}
                              className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-purple-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleCuisine(cuisine.id)}
                                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-800">{cuisine.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Currency
                </label>
                <select
                  value={formData.currencyId}
                  onChange={(e) => setFormData({ ...formData, currencyId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {!formData.currencyId && <option value="">Select Currency</option>}
                  {currencyOptions.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code} {currency.symbol ? `(${currency.symbol})` : ''} {currency.name ? `- ${currency.name}` : ''}
                    </option>
                  ))}
                </select>
                {currencyOptions.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">No currencies found in master data.</p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Price Range
                </label>
                <select
                  value={formData.priceRange}
                  onChange={(e) => setFormData({ ...formData, priceRange: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {!formData.priceRange && <option value="">Select Price Range</option>}
                  {priceRanges.map(range => (
                    <option key={range.id} value={range.id}>
                      {formatPriceRangeLabel(range)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Delivery Time
                </label>
                <input
                  type="text"
                  value={formData.deliveryTime}
                  onChange={(e) => setFormData({ ...formData, deliveryTime: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="30-45 mins"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Minimum Order ({currencySymbol})
                </label>
                <input
                  type="number"
                  value={formData.minimumOrder}
                  onChange={(e) => setFormData({ ...formData, minimumOrder: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Delivery Fee ({currencySymbol})
                </label>
                <input
                  type="number"
                  value={formData.deliveryFee}
                  onChange={(e) => setFormData({ ...formData, deliveryFee: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Packaging Fee ({currencySymbol})
                </label>
                <input
                  type="number"
                  value={formData.packagingFee}
                  onChange={(e) => setFormData({ ...formData, packagingFee: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Food Type
                </label>
                <div className="mb-2 relative" ref={foodTypeDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setFoodTypeDropdownOpen((prev) => !prev)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 flex items-center justify-between bg-white"
                  >
                    <span className={`${selectedFoodTypeNames.length === 0 ? 'text-gray-400' : 'text-gray-700'} truncate`}>
                      {foodTypeDropdownLabel}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${foodTypeDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {foodTypeDropdownOpen && (
                    <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl">
                      <div className="p-2 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <label className="inline-flex items-center justify-center px-2 py-2 border border-gray-300 rounded-lg cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={allVisibleFoodTypesSelected}
                              onChange={toggleAllVisibleFoodTypes}
                              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                          </label>
                          <div className="relative flex-1">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={foodTypeSearch}
                            onChange={(e) => setFoodTypeSearch(e.target.value)}
                            placeholder="Search food type..."
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                          {foodTypeSearch && (
                            <button
                              type="button"
                              onClick={() => setFoodTypeSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                          </div>
                        </div>
                      </div>
                      <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                        {filteredFoodTypeOptions.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">No food type found</div>
                        )}
                        {filteredFoodTypeOptions.map((option) => {
                          const checked = formData.foodType.includes(option.id);
                          return (
                            <label
                              key={option.id}
                              className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-purple-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleFoodType(option.id)}
                                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-800">{option.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Operating Hours */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              Operating Hours
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Opening Time
                </label>
                <input
                  type="time"
                  value={formData.openTime}
                  onChange={(e) => setFormData({ ...formData, openTime: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Closing Time
                </label>
                <input
                  type="time"
                  value={formData.closeTime}
                  onChange={(e) => setFormData({ ...formData, closeTime: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-gray-700 font-medium">Active Restaurant (Available for orders)</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {mode === 'edit' ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>
                  <Store className="w-5 h-5" />
                  {mode === 'edit' ? 'Save Changes' : 'Add Restaurant'}
                </>
              )}
            </button>
          </div>
        </form>

        <MapCoordinatePicker
          isOpen={showCoordinatePicker}
          onClose={() => setShowCoordinatePicker(false)}
          initialLocation={
            formData.latitude.trim() !== '' && formData.longitude.trim() !== '' &&
            !Number.isNaN(Number(formData.latitude)) && !Number.isNaN(Number(formData.longitude))
              ? { latitude: Number(formData.latitude), longitude: Number(formData.longitude) }
              : undefined
          }
          onConfirm={({ latitude, longitude }) => {
            setFormData((prev) => ({
              ...prev,
              latitude: String(latitude),
              longitude: String(longitude),
            }));
            setShowCoordinatePicker(false);
          }}
        />
      </div>
    </div>
  );
}


